"use client";

import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getAccessToken } from "@/lib/auth";
import { useUIStore, type HandoffBundle } from "@/stores/ui-store";

const WS_URL = process.env.NEXT_PUBLIC_WS_URL || "http://localhost:8000";

export function useSocket() {
  const socketRef = useRef<Socket | null>(null);
  const qc = useQueryClient();
  const setHandoffBundle = useUIStore((s) => s.setHandoffBundle);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const socket = io(WS_URL, {
      path: "/ws/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    socket.on("new_message", ({ conversation_id }: { conversation_id: string }) => {
      qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    });

    socket.on("lead_updated", ({ lead_id }: { lead_id: string }) => {
      qc.invalidateQueries({ queryKey: ["leads", lead_id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    });

    socket.on("conversation_state_changed", ({ conversation_id }: { conversation_id: string }) => {
      qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
    });

    socket.on("new_lead", () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    });

    socket.on("handoff_bundle", (bundle: HandoffBundle) => {
      setHandoffBundle(bundle);
      qc.invalidateQueries({ queryKey: ["conversations", bundle.conversation_id] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    });

    socket.on(
      "sentiment_alert",
      ({ conversation_id, lead_name, lead_phone, reason }: { conversation_id: string; lead_name: string | null; lead_phone: string | null; reason: string }) => {
        const name = lead_name ?? lead_phone ?? "Lead desconocido";
        toast.warning(`Señal negativa detectada — ${name}`, {
          description: reason,
          action: { label: "Ver", onClick: () => { window.location.href = `/conversations?lead_id=${conversation_id}`; } },
          duration: 8000,
        });
        qc.invalidateQueries({ queryKey: ["conversations", conversation_id] });
        qc.invalidateQueries({ queryKey: ["conversations"] });
      },
    );

    return () => {
      socket.disconnect();
    };
  }, [qc]);

  return socketRef.current;
}
